// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { auth } from '../firebase.config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { resolvePortalAccess, PORTAL_ACCESS } from './portalAccess';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // true only until Firebase Auth reports its (cached, local) session state.
  // The role check below must NOT hold this flag: {!loading && children}
  // gates the whole site, including the public pages and the invite page,
  // and the role needs a Firestore round-trip. ProtectedRoute waits on
  // accessChecked for /admin alone.
  const [loading, setLoading] = useState(true);
  // 'admin' | 'pending' | 'denied' | 'error' | null (null = signed out, or check in flight)
  const [access, setAccess] = useState(null);
  const [accessChecked, setAccessChecked] = useState(false);
  // Why the last session was dropped ('pending' | 'denied'), so /login can
  // explain instead of a silent bounce. Cleared by /login once shown.
  const [deniedReason, setDeniedReason] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Monotonic run id: only the LATEST auth-state callback may publish its
    // role result. The uid comparison alone is not enough - /login signs out
    // and back in for a same-account retry, so two checks for the same uid
    // can overlap and a slow first one must not overwrite the second.
    let runId = 0;
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      const myRun = ++runId;
      setUser(nextUser);
      setLoading(false);
      if (!nextUser) {
        setAccess(null);
        setAccessChecked(true);
        return;
      }
      // Firebase Auth accepts any account of the project; only root
      // users/{uid}.role === 'admin' may use the portal (2026-09-07).
      setAccessChecked(false);
      const result = await resolvePortalAccess(nextUser.uid);
      // Stale-callback guard: the session may have changed or ended while
      // the read was in flight - never stamp user A's result onto user B,
      // nor an older check's result onto a newer one.
      if (cancelled || myRun !== runId || !auth.currentUser || auth.currentUser.uid !== nextUser.uid) return;
      setAccess(result);
      setAccessChecked(true);
      if (result === PORTAL_ACCESS.DENIED || result === PORTAL_ACCESS.PENDING) {
        // A non-admin account obtained a session on the portal's default
        // app - drop it at once so no /admin surface ever mounts.
        setDeniedReason(result);
        signOut(auth).catch(() => {});
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const clearDenied = useCallback(() => setDeniedReason(null), []);

  const value = {
    user,
    loading,
    access,
    accessChecked,
    isAdmin: access === PORTAL_ACCESS.ADMIN,
    deniedReason,
    clearDenied,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
